import { resolveLogo, useBranding } from "@/contexts/BrandingContext";
import PassportVisaScene from "./PassportVisaScene";

/**
 * LandingBackdrop
 * Premium decorative layer for all portal pages.
 *
 * Variants:
 *   - "hero"    → full backdrop with passport-opens-visa cinematic + watermark + marks.
 *                 Use only on the portal landing page.
 *   - "ambient" → calm version: scattered passport / stamp / boarding-pass marks
 *                 + a soft logo watermark. Used on submit / track / detail pages
 *                 so the brand feel carries through without distracting from forms.
 */
export default function LandingBackdrop({ theme, variant = "hero" }) {
  const { branding } = useBranding();
  const logoUrl = resolveLogo(branding.logo_url);
  const isDark = theme.name === "dark";
  const faint = isDark ? "rgba(212,175,55,0.06)" : "rgba(166,124,26,0.09)";

  return (
    <div
      aria-hidden
      style={{
        // Hero variant overlays its parent (PortalLanding's full-width flex column).
        // Ambient variant is fixed to the viewport so it spans behind centered
        // forms like /submit and /track regardless of their max-width.
        position: variant === "ambient" ? "fixed" : "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: 0,
      }}
    >
      {/* Faint scattered passport / stamp / boarding-pass marks */}
      <FaintMarks color={faint} dense={variant === "ambient"} />

      {/* Hero-only: the cinematic passport + visa + stamp scene */}
      {variant === "hero" && <PassportVisaScene theme={theme} />}

      {/* Soft centered logo watermark */}
      <img
        src={logoUrl}
        alt=""
        style={{
          position: "absolute",
          top: variant === "hero" ? "62%" : "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: variant === "hero" ? "min(280px, 32vw)" : "min(360px, 38vw)",
          height: variant === "hero" ? "min(280px, 32vw)" : "min(360px, 38vw)",
          objectFit: "contain",
          opacity: isDark ? 0.05 : 0.07,
        }}
      />
    </div>
  );
}

/* ----------------------- Faint passport / stamp marks ----------------------- */
function FaintMarks({ color, dense }) {
  const baseMarks = [
    { top: "4%",  left: "4%",  rotate: -10, kind: "passport", scale: 1 },
    { top: "6%",  right: "5%", rotate: 14,  kind: "stamp",    scale: 1 },
    { top: "30%", left: "2%",  rotate: 6,   kind: "boarding", scale: 1 },
    { top: "32%", right: "3%", rotate: -8,  kind: "passport", scale: 0.9 },
    { top: "55%", left: "6%",  rotate: 12,  kind: "stamp",    scale: 0.9 },
    { top: "58%", right: "7%", rotate: -4,  kind: "boarding", scale: 1 },
    { bottom: "12%", left: "3%",  rotate: -14, kind: "passport", scale: 0.95 },
    { bottom: "8%",  left: "30%", rotate: 8,   kind: "stamp",    scale: 0.8 },
    { bottom: "14%", right: "5%", rotate: 10,  kind: "boarding", scale: 0.95 },
    { bottom: "6%",  right: "32%", rotate: -10, kind: "passport", scale: 0.8 },
  ];

  // Form pages get a few extra marks since the cinematic scene isn't there.
  const extraMarks = dense
    ? [
        { top: "18%", left: "42%", rotate: -6, kind: "stamp",    scale: 0.7 },
        { top: "70%", left: "44%", rotate: 4,  kind: "boarding", scale: 0.75 },
      ]
    : [];

  const marks = [...baseMarks, ...extraMarks];

  return (
    <>
      {marks.map((m, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: m.top, left: m.left, right: m.right, bottom: m.bottom,
            transform: `rotate(${m.rotate}deg) scale(${m.scale})`,
            opacity: 0.95,
          }}
        >
          <Mark kind={m.kind} color={color} />
        </div>
      ))}
    </>
  );
}

function Mark({ kind, color }) {
  if (kind === "passport") {
    return (
      <svg width={110} height={140} viewBox="0 0 110 140" fill="none">
        <rect x="3" y="3" width="104" height="134" rx="10" stroke={color} strokeWidth="1.5" />
        <circle cx="55" cy="58" r="16" stroke={color} strokeWidth="1.2" />
        <path d="M30 92 H80 M30 102 H70 M30 112 H75" stroke={color} strokeWidth="1.2" />
      </svg>
    );
  }
  if (kind === "stamp") {
    return (
      <svg width={120} height={120} viewBox="0 0 120 120" fill="none">
        <circle cx="60" cy="60" r="48" stroke={color} strokeWidth="1.5" strokeDasharray="4 4" />
        <circle cx="60" cy="60" r="34" stroke={color} strokeWidth="1.2" />
        <path d="M40 60 H80 M60 44 V76" stroke={color} strokeWidth="1.2" />
      </svg>
    );
  }
  return (
    <svg width={160} height={80} viewBox="0 0 160 80" fill="none">
      <rect x="3" y="3" width="120" height="74" rx="6" stroke={color} strokeWidth="1.5" />
      <path d="M123 3 V77" stroke={color} strokeWidth="1.5" strokeDasharray="3 3" />
      <rect x="130" y="14" width="26" height="52" rx="3" stroke={color} strokeWidth="1.2" />
      <path d="M14 22 H80 M14 36 H70 M14 50 H58" stroke={color} strokeWidth="1.2" />
    </svg>
  );
}
